// Call plugin.
import * as actionTypes from '../actionTypes'

// Libraries.
import { isEmpty, isPlainObject } from 'lodash/fp'

/**
 * Helper function for formatting call actions.
 * @method callActionHelper
 * @param  {string} type Action type.
 * @param  {string} id Id of the call being acted on.
 * @param  {Object} [payload={}]
 * @param  {BasicError} [payload.error]
 * @param  {Object} [meta={}]
 * @return {Object} An action.
 */
export function callActionHelper (type, id, payload = {}, meta = {}) {
  /**
   * Call action format.
   * Call actions are not FSA-compliant because there are cases where we need
   *    to provide useful info (for the SDK) and an error object (for the app).
   *    FSA would require the error object to be the entirety of the payload.
   * @type {Object}
   * {
   *   type,
   *   payload: {
   *    ...usefulInfo,
   *    errorObject
   *   },
   *   error,
   *   meta
   * }
   */
  const action = {
    type,
    payload: {
      ...payload,
      id
    }
  }
  // Only have meta and error properties on the action if they're needed.
  if (!isEmpty(meta)) {
    action.meta = meta
  }
  if (payload.error) {
    action.error = true
  }
  return action
}

export function makeCall (id, options) {
  return callActionHelper(actionTypes.MAKE_CALL, id, options)
}

export function sessionCreated (id, params) {
  return callActionHelper(actionTypes.SESSION_CREATED, id, params)
}

export function pendingMakeCall (id, options) {
  return callActionHelper(actionTypes.PENDING_MAKE_CALL, id, options)
}

export function makeCallFinish (id, params) {
  return callActionHelper(actionTypes.MAKE_CALL_FINISH, id, params)
}

export function makeAnonymousCall (id, params) {
  return {
    type: actionTypes.MAKE_ANONYMOUS_CALL,
    payload: {
      id,
      callee: params.callee,
      credentials: params.credentials,
      mediaConstraints: params.mediaConstraints,
      callOptions: params.callOptions
    }
  }
}

export function makeAnonymousCallFinish (id, params) {
  return callActionHelper(actionTypes.MAKE_ANONYMOUS_CALL_FINISH, id, params)
}

export function callIncoming (id, params) {
  return callActionHelper(actionTypes.CALL_INCOMING, id, params)
}

export function sendRingingFeedback (id) {
  return callActionHelper(actionTypes.SEND_RINGING_FEEDBACK, id)
}

export function sendRingingFeedbackFinish (id, params) {
  return callActionHelper(actionTypes.SEND_RINGING_FEEDBACK_FINISH, id, params)
}

export function callRinging (id, params) {
  return callActionHelper(actionTypes.CALL_RINGING, id, params)
}

export function sessionProgress (id, params) {
  return callActionHelper(actionTypes.SESSION_PROGRESS, id, params)
}

export function callCancelled (id, params) {
  return callActionHelper(actionTypes.CALL_CANCELLED, id, params)
}

export function answerCall (id, options) {
  return callActionHelper(actionTypes.ANSWER_CALL, id, options)
}

export function answerCallFinish (id, params, meta) {
  return callActionHelper(actionTypes.ANSWER_CALL_FINISH, id, params, meta)
}

export function rejectCall (id, options) {
  return callActionHelper(actionTypes.REJECT_CALL, id, options)
}

export function rejectCallFinish (id, params) {
  return callActionHelper(actionTypes.REJECT_CALL_FINISH, id, params)
}

export function callAccepted (id, params) {
  return callActionHelper(actionTypes.CALL_ACCEPTED, id, params)
}

export function ignoreCall (id, params) {
  return callActionHelper(actionTypes.IGNORE_CALL, id, params)
}

export function ignoreCallFinish (id, params) {
  return callActionHelper(actionTypes.IGNORE_CALL_FINISH, id, params)
}

export function endCall (id, params) {
  return callActionHelper(actionTypes.END_CALL, id, params)
}

export function endCallFinish (id, params) {
  return callActionHelper(actionTypes.END_CALL_FINISH, id, params)
}

export function holdCall (id, options) {
  return callActionHelper(actionTypes.CALL_HOLD, id, options)
}

export function holdCallFinish (id, params) {
  return callActionHelper(actionTypes.CALL_HOLD_FINISH, id, params)
}

export function unholdCall (id, options) {
  return callActionHelper(actionTypes.CALL_UNHOLD, id, options)
}

export function unholdCallFinish (id, params) {
  return callActionHelper(actionTypes.CALL_UNHOLD_FINISH, id, params)
}

export function setCustomParameters (id, customOptions) {
  const action = {
    type: actionTypes.SET_CUSTOM_PARAMETERS,
    payload: {
      id,
      // customOptions can either be an array (i.e., just the headers) or an object (i.e., headers and body)
      customParameters: isPlainObject(customOptions) ? customOptions.customParameters : customOptions,
      customBodies: isPlainObject(customOptions) ? customOptions.customBodies : undefined
    }
  }
  return action
}

export function sendCustomParameters (id, options) {
  return callActionHelper(actionTypes.SEND_CUSTOM_PARAMETERS, id, options)
}

export function sendCustomParametersFinish (id, params) {
  return callActionHelper(actionTypes.SEND_CUSTOM_PARAMETERS_FINISH, id, params)
}

export function customParametersReceived (id, params) {
  return callActionHelper(actionTypes.CUSTOM_PARAMETERS_RECEIVED, id, params)
}

export function addMedia (id, params) {
  return callActionHelper(actionTypes.ADD_MEDIA, id, params)
}

export function addMediaFinish (id, params) {
  return callActionHelper(actionTypes.ADD_MEDIA_FINISH, id, params)
}

export function addBasicMedia (id, params) {
  return callActionHelper(actionTypes.ADD_BASIC_MEDIA, id, params)
}

export function removeMedia (id, params) {
  return callActionHelper(actionTypes.REMOVE_MEDIA, id, params)
}

export function removeMediaFinish (id, params) {
  return callActionHelper(actionTypes.REMOVE_MEDIA_FINISH, id, params)
}

export function removeBasicMedia (id, params) {
  return callActionHelper(actionTypes.REMOVE_BASIC_MEDIA, id, params)
}

export function sendDTMF (id, params) {
  return callActionHelper(actionTypes.SEND_DTMF, id, params)
}

export function sendDTMFFinish (id, params) {
  return callActionHelper(actionTypes.SEND_DTMF_FINISH, id, params)
}

export function getStats (id, params, deferred) {
  const action = {
    type: actionTypes.GET_STATS,
    payload: {
      ...params,
      id
    },
    meta: {
      deferred
    }
  }

  return action
}

export function getStatsFinish (id, params) {
  return callActionHelper(actionTypes.GET_STATS_FINISH, id, params)
}

export function forwardCall (id, params) {
  return callActionHelper(actionTypes.FORWARD_CALL, id, params)
}

export function forwardCallFinish (id, params) {
  return callActionHelper(actionTypes.FORWARD_CALL_FINISH, id, params)
}

export function consultativeTransfer (id, params) {
  return callActionHelper(actionTypes.CONSULTATIVE_TRANSFER, id, params)
}

export function pendingConsultativeTransfer (id, params) {
  return callActionHelper(actionTypes.PENDING_CONSULTATIVE_TRANSFER, id, params)
}

export function consultativeTransferFinish (id, params) {
  return callActionHelper(actionTypes.CONSULTATIVE_TRANSFER_FINISH, id, params)
}

export function directTransfer (id, params) {
  return callActionHelper(actionTypes.DIRECT_TRANSFER, id, params)
}

export function directTransferFinish (id, params) {
  return callActionHelper(actionTypes.DIRECT_TRANSFER_FINISH, id, params)
}

export function join (id, params) {
  return callActionHelper(actionTypes.JOIN, id, params)
}

export function joinStart (id, params) {
  return callActionHelper(actionTypes.JOIN_START, id, params)
}

export function pendingJoin (id, params) {
  return callActionHelper(actionTypes.PENDING_JOIN, id, params)
}

export function joinFinish (id, params) {
  return callActionHelper(actionTypes.JOIN_FINISH, id, params)
}

export function replaceTrack (id, params) {
  return callActionHelper(actionTypes.REPLACE_TRACK, id, params)
}

export function replaceTrackFinish (id, params) {
  return callActionHelper(actionTypes.REPLACE_TRACK_FINISH, id, params)
}

export function pendingOperation (id, params) {
  return callActionHelper(actionTypes.PENDING_OPERATION, id, params)
}

export function getAvailableCodecs (params, deferred) {
  const action = {
    type: actionTypes.GET_AVAILABLE_CODECS,
    payload: {
      ...params
    },
    meta: {
      deferred
    }
  }
  return action
}

export function availableCodecsRetrieved (params) {
  return callActionHelper(actionTypes.AVAILABLE_CODECS_RETRIEVED, undefined, params)
}

/*
 * Remote operations.
 */

export function remoteHoldFinish (id, params) {
  return callActionHelper(actionTypes.CALL_REMOTE_HOLD_FINISH, id, params)
}

export function remoteUnholdFinish (id, params) {
  return callActionHelper(actionTypes.CALL_REMOTE_UNHOLD_FINISH, id, params)
}

export function remoteAddMediaFinish (id, params) {
  return callActionHelper(actionTypes.REMOTE_ADD_MEDIA_FINISH, id, params)
}

export function remoteRemoveMediaFinish (id, params) {
  return callActionHelper(actionTypes.REMOTE_REMOVE_MEDIA_FINISH, id, params)
}

export function remoteStartMohFinish (id, params) {
  return callActionHelper(actionTypes.REMOTE_START_MOH_FINISH, id, params)
}

export function remoteStopMohFinish (id, params) {
  return callActionHelper(actionTypes.REMOTE_STOP_MOH_FINISH, id, params)
}

export function remoteSlowStart (id, params) {
  return callActionHelper(actionTypes.REMOTE_SLOW_START, id, params)
}

export function restartMedia (id, params) {
  return callActionHelper(actionTypes.MEDIA_RESTART, id, params)
}

export function restartMediaFinish (id, params) {
  return callActionHelper(actionTypes.MEDIA_RESTART_FINISH, id, params)
}

export function resync (id, params) {
  return callActionHelper(actionTypes.RESYNC, id, params)
}

export function resyncFinish (id, params) {
  return callActionHelper(actionTypes.RESYNC_FINISH, id, params)
}
// Generic action.
export function updateCall (id, params) {
  return callActionHelper(actionTypes.UPDATE_CALL, id, params)
}
